import { object, number, boolean, mixed, string, array } from 'yup';

import type {
  ToleranceType,
  DimensionType,
  DepthType,
  MaterialCondition,
  ControlFrameCharacteristic,
} from '@frontend/api';
import {
  TOLERANCE_TYPES,
  DIMENSION_TYPES,
  DEPTH_TYPES,
  MATERIAL_CONDITIONS,
  CONTROL_FRAME_CHARACTERISTICS,
} from '@frontend/api';

/**
 * Represents a numeric value with precision information
 */
export type PrecisionValue = {
  /** The string representation as entered by the user */
  stringValue: string;
  /** The parsed numeric value */
  numericValue: number;
  /** Number of decimal places (0 for integers) */
  precision: number;
};

/**
 * Parses a string input into a PrecisionValue
 */
export const parsePrecisionValue = (input: string): PrecisionValue | null => {
  const trimmed = input.trim();
  if (trimmed === '' || trimmed === '-' || trimmed === '.') {
    return null;
  }

  const numericValue = parseFloat(trimmed);
  if (isNaN(numericValue)) {
    return null;
  }

  // Calculate precision by counting decimal places
  const decimalIndex = trimmed.indexOf('.');
  const precision = decimalIndex === -1 ? 0 : trimmed.length - decimalIndex - 1;

  return {
    stringValue: trimmed,
    numericValue,
    precision,
  };
};

/**
 * Validates if a string represents a valid number
 */
export const isValidNumberString = (input: string): boolean => {
  const trimmed = input.trim();
  if (trimmed === '' || trimmed === '-' || trimmed === '.') {
    return false;
  }
  return !isNaN(parseFloat(trimmed));
};

/**
 * Creates a required string-based number field validation with minimum value constraint
 */
export const createRequiredStringNumberField = (
  fieldName: string,
  minValue = 0,
) =>
  string()
    .required(`${fieldName} is required`)
    .test('is-valid-number', `${fieldName} must be a valid number`, (value) => {
      if (!value) return false;
      return isValidNumberString(value);
    })
    .test('min-value', `${fieldName} must be at least ${minValue}`, (value) => {
      if (!value) return true; // Let required validation handle empty values
      const parsed = parsePrecisionValue(value);
      return parsed ? parsed.numericValue >= minValue : false;
    });

/**
 * Creates a multiplier validation field
 */
const createMultiplierValidation = () =>
  number()
    .typeError('Multiplier must be a number')
    .integer('Multiplier must be a whole number')
    .min(1, 'Multiplier must be at least 1')
    .when('hasMult', {
      is: true,
      then: (schema) => schema.required('Multiplier is required when enabled'),
      otherwise: (schema) => schema.notRequired(),
    });

/**
 * Creates a boolean field validation
 */
export const createBooleanField = () => boolean().required();

/**
 * Creates tolerance type validation
 */
export const createToleranceTypeValidation = (
  hasToleranceField = 'hasTolerance',
) =>
  mixed<ToleranceType>()
    .oneOf(
      Object.values(TOLERANCE_TYPES),
      'Please select a valid tolerance type',
    )
    .when(hasToleranceField, {
      is: true,
      then: (schema) =>
        schema.required('Tolerance type is required when tolerance is enabled'),
      otherwise: (schema) => schema.notRequired(),
    });

/**
 * Creates string-based symmetric tolerance validation
 */
export const createStringSymmetricToleranceValidation = (
  hasToleranceField = 'hasTolerance',
  toleranceTypeField = 'toleranceType',
) =>
  string().when([hasToleranceField, toleranceTypeField], {
    is: (hasTolerance: boolean, toleranceType: ToleranceType) =>
      hasTolerance && toleranceType === TOLERANCE_TYPES.SYMMETRIC,
    then: (schema) =>
      schema
        .required('Symmetric tolerance value is required')
        .test(
          'is-valid-number',
          'Symmetric tolerance must be a valid number',
          (value) => {
            if (!value) return false;
            return isValidNumberString(value);
          },
        )
        .test(
          'min-value',
          'Symmetric tolerance must be non-negative',
          (value) => {
            if (!value) return true;
            const parsed = parsePrecisionValue(value);
            return parsed ? parsed.numericValue >= 0 : false;
          },
        ),
    otherwise: (schema) => schema.notRequired(),
  });

/**
 * Creates a string-based tolerance validation group for precision-aware values
 */
export const createStringToleranceValidationGroup = (prefix = '') => {
  const hasToleranceField = prefix ? `${prefix}HasTolerance` : 'hasTolerance';
  const toleranceTypeField = prefix
    ? `${prefix}ToleranceType`
    : 'toleranceType';
  const symmetricField = prefix
    ? `${prefix}ToleranceSymmetric`
    : 'toleranceSymmetric';
  const upperField = prefix ? `${prefix}ToleranceUpper` : 'toleranceUpper';
  const lowerField = prefix ? `${prefix}ToleranceLower` : 'toleranceLower';

  return {
    [hasToleranceField]: createBooleanField(),
    [toleranceTypeField]: createToleranceTypeValidation(hasToleranceField),
    [symmetricField]: createStringSymmetricToleranceValidation(
      hasToleranceField,
      toleranceTypeField,
    ),
    [upperField]: string().when([hasToleranceField, toleranceTypeField], {
      is: (hasTolerance: boolean, toleranceType: ToleranceType) =>
        hasTolerance &&
        (toleranceType === TOLERANCE_TYPES.ASYMMETRIC ||
          toleranceType === TOLERANCE_TYPES.LIMIT),
      then: (schema) =>
        schema
          .required('Upper tolerance value is required')
          .test(
            'is-valid-number',
            'Upper tolerance must be a valid number',
            (value) => {
              if (!value) return false;
              return isValidNumberString(value);
            },
          )
          .test(
            'min-value',
            'Upper tolerance must be non-negative',
            (value) => {
              if (!value) return true;
              const parsed = parsePrecisionValue(value);
              return parsed ? parsed.numericValue >= 0 : false;
            },
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
    [lowerField]: string().when([hasToleranceField, toleranceTypeField], {
      is: (hasTolerance: boolean, toleranceType: ToleranceType) =>
        hasTolerance &&
        (toleranceType === TOLERANCE_TYPES.ASYMMETRIC ||
          toleranceType === TOLERANCE_TYPES.LIMIT),
      then: (schema) =>
        schema
          .required('Lower tolerance value is required')
          .test(
            'is-valid-number',
            'Lower tolerance must be a valid number',
            (value) => {
              if (!value) return false;
              return isValidNumberString(value);
            },
          )
          .test(
            'min-value',
            'Lower tolerance must be non-negative',
            (value) => {
              if (!value) return true;
              const parsed = parsePrecisionValue(value);
              return parsed ? parsed.numericValue >= 0 : false;
            },
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
  };
};

/**
 * Creates string-based depth value validation
 */
export const createStringDepthValueValidation = (
  hasDepthField = 'hasDepth',
  depthTypeField = 'depthType',
) =>
  string().when([hasDepthField, depthTypeField], {
    is: (hasDepth: boolean, depthType: DepthType) =>
      hasDepth && depthType === DEPTH_TYPES.DEPTH,
    then: (schema) =>
      schema
        .required('Depth value is required')
        .test(
          'is-valid-number',
          'Depth value must be a valid number',
          (value) => {
            if (!value) return false;
            return isValidNumberString(value);
          },
        )
        .test('min-value', 'Depth value must be positive', (value) => {
          if (!value) return true;
          const parsed = parsePrecisionValue(value);
          return parsed ? parsed.numericValue > 0 : false;
        }),
    otherwise: (schema) => schema.notRequired(),
  });

/**
 * Creates string-based depth tolerance validation group
 */
export const createStringDepthToleranceValidationGroup = (prefix = 'depth') => {
  const hasDepthField = 'hasDepth';
  const depthTypeField = 'depthType';
  const hasToleranceField = `${prefix}HasTolerance`;
  const toleranceTypeField = `${prefix}ToleranceType`;
  const symmetricField = `${prefix}ToleranceSymmetric`;
  const upperField = `${prefix}ToleranceUpper`;
  const lowerField = `${prefix}ToleranceLower`;

  return {
    [hasToleranceField]: boolean().when([hasDepthField, depthTypeField], {
      is: (hasDepth: boolean, depthType: DepthType) =>
        hasDepth && depthType === DEPTH_TYPES.DEPTH,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired(),
    }),
    [toleranceTypeField]: mixed<ToleranceType>()
      .oneOf(
        Object.values(TOLERANCE_TYPES),
        'Please select a valid tolerance type',
      )
      .when([hasDepthField, depthTypeField, hasToleranceField], {
        is: (hasDepth: boolean, depthType: DepthType, hasTolerance: boolean) =>
          hasDepth && depthType === DEPTH_TYPES.DEPTH && hasTolerance,
        then: (schema) =>
          schema.required(
            'Tolerance type is required when tolerance is enabled',
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
    [symmetricField]: string().when(
      [hasDepthField, depthTypeField, hasToleranceField, toleranceTypeField],
      {
        is: (
          hasDepth: boolean,
          depthType: DepthType,
          hasTolerance: boolean,
          toleranceType: ToleranceType,
        ) =>
          hasDepth &&
          depthType === DEPTH_TYPES.DEPTH &&
          hasTolerance &&
          toleranceType === TOLERANCE_TYPES.SYMMETRIC,
        then: (schema) =>
          schema
            .required('Symmetric tolerance value is required')
            .test(
              'is-valid-number',
              'Symmetric tolerance must be a valid number',
              (value) => {
                if (!value) return false;
                return isValidNumberString(value);
              },
            )
            .test(
              'min-value',
              'Symmetric tolerance must be non-negative',
              (value) => {
                if (!value) return true;
                const parsed = parsePrecisionValue(value);
                return parsed ? parsed.numericValue >= 0 : false;
              },
            ),
        otherwise: (schema) => schema.notRequired(),
      },
    ),
    [upperField]: string().when(
      [hasDepthField, depthTypeField, hasToleranceField, toleranceTypeField],
      {
        is: (
          hasDepth: boolean,
          depthType: DepthType,
          hasTolerance: boolean,
          toleranceType: ToleranceType,
        ) =>
          hasDepth &&
          depthType === DEPTH_TYPES.DEPTH &&
          hasTolerance &&
          (toleranceType === TOLERANCE_TYPES.ASYMMETRIC ||
            toleranceType === TOLERANCE_TYPES.LIMIT),
        then: (schema) =>
          schema
            .required('Upper tolerance value is required')
            .test(
              'is-valid-number',
              'Upper tolerance must be a valid number',
              (value) => {
                if (!value) return false;
                return isValidNumberString(value);
              },
            )
            .test(
              'min-value',
              'Upper tolerance must be non-negative',
              (value) => {
                if (!value) return true;
                const parsed = parsePrecisionValue(value);
                return parsed ? parsed.numericValue >= 0 : false;
              },
            ),
        otherwise: (schema) => schema.notRequired(),
      },
    ),
    [lowerField]: string().when(
      [hasDepthField, depthTypeField, hasToleranceField, toleranceTypeField],
      {
        is: (
          hasDepth: boolean,
          depthType: DepthType,
          hasTolerance: boolean,
          toleranceType: ToleranceType,
        ) =>
          hasDepth &&
          depthType === DEPTH_TYPES.DEPTH &&
          hasTolerance &&
          (toleranceType === TOLERANCE_TYPES.ASYMMETRIC ||
            toleranceType === TOLERANCE_TYPES.LIMIT),
        then: (schema) =>
          schema
            .required('Lower tolerance value is required')
            .test(
              'is-valid-number',
              'Lower tolerance must be a valid number',
              (value) => {
                if (!value) return false;
                return isValidNumberString(value);
              },
            )
            .test(
              'min-value',
              'Lower tolerance must be non-negative',
              (value) => {
                if (!value) return true;
                const parsed = parsePrecisionValue(value);
                return parsed ? parsed.numericValue >= 0 : false;
              },
            ),
        otherwise: (schema) => schema.notRequired(),
      },
    ),
  };
};

/**
 * Creates string-based angle value validation
 */
export const createStringAngleValueValidation = (hasAngleField = 'hasAngle') =>
  string().when(hasAngleField, {
    is: true,
    then: (schema) =>
      schema
        .required('Angle value is required')
        .test(
          'is-valid-number',
          'Angle value must be a valid number',
          (value) => {
            if (!value) return false;
            return isValidNumberString(value);
          },
        )
        .test('min-value', 'Angle value must be positive', (value) => {
          if (!value) return true;
          const parsed = parsePrecisionValue(value);
          return parsed ? parsed.numericValue > 0 : false;
        })
        .test(
          'max-value',
          'Angle value must be 180 degrees or less',
          (value) => {
            if (!value) return true;
            const parsed = parsePrecisionValue(value);
            return parsed ? parsed.numericValue <= 180 : false;
          },
        ),
    otherwise: (schema) => schema.notRequired(),
  });

/**
 * Creates string-based angle tolerance validation group
 */
export const createStringAngleToleranceValidationGroup = (prefix = 'angle') => {
  const hasAngleField = 'hasAngle';
  const hasToleranceField = `${prefix}HasTolerance`;
  const toleranceTypeField = `${prefix}ToleranceType`;
  const symmetricField = `${prefix}ToleranceSymmetric`;
  const upperField = `${prefix}ToleranceUpper`;
  const lowerField = `${prefix}ToleranceLower`;

  return {
    [hasToleranceField]: boolean().when(hasAngleField, {
      is: true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired(),
    }),
    [toleranceTypeField]: mixed<ToleranceType>()
      .oneOf(
        Object.values(TOLERANCE_TYPES),
        'Please select a valid tolerance type',
      )
      .when([hasAngleField, hasToleranceField], {
        is: (hasAngle: boolean, hasTolerance: boolean) =>
          hasAngle && hasTolerance,
        then: (schema) =>
          schema.required(
            'Tolerance type is required when tolerance is enabled',
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
    [symmetricField]: string().when(
      [hasAngleField, hasToleranceField, toleranceTypeField],
      {
        is: (
          hasAngle: boolean,
          hasTolerance: boolean,
          toleranceType: ToleranceType,
        ) =>
          hasAngle &&
          hasTolerance &&
          toleranceType === TOLERANCE_TYPES.SYMMETRIC,
        then: (schema) =>
          schema
            .required('Symmetric tolerance value is required')
            .test(
              'is-valid-number',
              'Symmetric tolerance must be a valid number',
              (value) => {
                if (!value) return false;
                return isValidNumberString(value);
              },
            )
            .test(
              'min-value',
              'Symmetric tolerance must be non-negative',
              (value) => {
                if (!value) return true;
                const parsed = parsePrecisionValue(value);
                return parsed ? parsed.numericValue >= 0 : false;
              },
            ),
        otherwise: (schema) => schema.notRequired(),
      },
    ),
    [upperField]: string().when(
      [hasAngleField, hasToleranceField, toleranceTypeField],
      {
        is: (
          hasAngle: boolean,
          hasTolerance: boolean,
          toleranceType: ToleranceType,
        ) =>
          hasAngle &&
          hasTolerance &&
          (toleranceType === TOLERANCE_TYPES.ASYMMETRIC ||
            toleranceType === TOLERANCE_TYPES.LIMIT),
        then: (schema) =>
          schema
            .required('Upper tolerance value is required')
            .test(
              'is-valid-number',
              'Upper tolerance must be a valid number',
              (value) => {
                if (!value) return false;
                return isValidNumberString(value);
              },
            )
            .test(
              'min-value',
              'Upper tolerance must be non-negative',
              (value) => {
                if (!value) return true;
                const parsed = parsePrecisionValue(value);
                return parsed ? parsed.numericValue >= 0 : false;
              },
            ),
        otherwise: (schema) => schema.notRequired(),
      },
    ),
    [lowerField]: string().when(
      [hasAngleField, hasToleranceField, toleranceTypeField],
      {
        is: (
          hasAngle: boolean,
          hasTolerance: boolean,
          toleranceType: ToleranceType,
        ) =>
          hasAngle &&
          hasTolerance &&
          (toleranceType === TOLERANCE_TYPES.ASYMMETRIC ||
            toleranceType === TOLERANCE_TYPES.LIMIT),
        then: (schema) =>
          schema
            .required('Lower tolerance value is required')
            .test(
              'is-valid-number',
              'Lower tolerance must be a valid number',
              (value) => {
                if (!value) return false;
                return isValidNumberString(value);
              },
            )
            .test(
              'min-value',
              'Lower tolerance must be non-negative',
              (value) => {
                if (!value) return true;
                const parsed = parsePrecisionValue(value);
                return parsed ? parsed.numericValue >= 0 : false;
              },
            ),
        otherwise: (schema) => schema.notRequired(),
      },
    ),
  };
};

/**
 * Creates dimension type validation
 */
export const createDimensionTypeValidation = () =>
  mixed<DimensionType>()
    .oneOf(
      Object.values(DIMENSION_TYPES),
      'Please select a valid dimension type',
    )
    .required('Dimension type is required');

/**
 * Creates depth type validation
 */
export const createDepthTypeValidation = (hasDepthField = 'hasDepth') =>
  mixed<DepthType>()
    .oneOf(Object.values(DEPTH_TYPES), 'Please select a valid depth type')
    .when(hasDepthField, {
      is: true,
      then: (schema) =>
        schema.required('Depth type is required when depth is enabled'),
      otherwise: (schema) => schema.notRequired(),
    });

/**
 * Creates material condition validation
 */
export const createMaterialConditionValidation = (
  hasMatConField = 'hasMatCon',
) =>
  mixed<MaterialCondition>()
    .oneOf(
      Object.values(MATERIAL_CONDITIONS),
      'Please select a valid material condition',
    )
    .when(hasMatConField, {
      is: true,
      then: (schema) =>
        schema.required('Material condition is required when enabled'),
      otherwise: (schema) => schema.notRequired(),
    });

/**
 * Creates control frame characteristic validation
 */
export const createCharacteristicValidation = () =>
  mixed<ControlFrameCharacteristic>()
    .oneOf(
      Object.keys(
        CONTROL_FRAME_CHARACTERISTICS,
      ) as ControlFrameCharacteristic[],
      'Please select a valid characteristic',
    )
    .required('Characteristic is required');

/**
 * Creates datum letter validation
 */
export const createDatumLetterValidation = () =>
  string()
    .required('Datum reference is required')
    .matches(/^[A-Z]$/, 'Datum must be a single uppercase letter');

/**
 * Creates datum validation schema for datum references
 */
const createDatumValidationSchema = () =>
  object().shape({
    datum: createDatumLetterValidation(),
    hasMatCon: createBooleanField(),
    matCon: createMaterialConditionValidation('hasMatCon'),
  });

/**
 * Creates unique datums array validation
 */
export const createUniqueDatumsValidation = () =>
  array()
    .of(createDatumValidationSchema())
    .required()
    .test(
      'unique-datums',
      'All datum references must be unique',
      function (datums) {
        if (!datums || datums.length === 0) return true;

        // Get all non-empty datum letters
        const datumLetters = datums
          .map((datum) => datum.datum)
          .filter((letter) => letter && letter.trim() !== '');

        // Check if there are duplicates
        const uniqueLetters = new Set(datumLetters);
        if (datumLetters.length !== uniqueLetters.size) {
          return this.createError({
            message:
              'Datum references must be unique. Remove duplicate letters.',
          });
        }

        return true;
      },
    );

/**
 * Creates multiplier validation group
 */
export const createMultiplierValidationGroup = () => ({
  hasMult: createBooleanField(),
  mult: createMultiplierValidation(),
});
